import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, switchMap, tap } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CompanyData {
  legal_name: string;
  trade_name: string;
  cnpj: string;
  logo: string | null;
  annual_revenue_flag?: boolean | null;
  number_employees_flag?: boolean | null;
  /** Galeria (GET info / após upload); formato segue a API de membro. */
  images?: Array<{
    id?: number;
    url?: string | { url?: string | null } | null;
    image_url?: string | { url?: string | null } | null;
    position: number;
    description?: string | null;
  }>;
}

export interface MemberData {
  id: string;
  name: string;
  email: string;
  avatar?: string | { url: string } | null;
  token?: string;
  /** JWT de renovação — apenas para POST /authentication/refresh_token */
  refresh_token?: string;
  access_expires_in?: number;
  refresh_expires_in?: number;
  companies?: CompanyData[];
  profile_completion_percent?: number;
  completed_profile_flag?: boolean;
  is_approved?: boolean;
  created_at?: string;
  [key: string]: unknown;
}

export interface LoginResponse {
  status: string;
  message: string;
  content: MemberData;
}

export interface CreateMemberPayload {
  name: string;
  cpf: string;
  company_name: string;
  cnpj: string;
  phone: string;
  email: string;
  show_phone: boolean;
  show_email: boolean;
  instagram?: string;
  average_annual_revenue?: number;
  employees_count?: number;
  industry_sector?: string;
  professional_activity?: string;
  password: string;
  password_confirmation: string;
  accept_terms: boolean;
  avatar?: File;
}

export interface CreateMemberResponse {
  status: string;
  message: string;
  content?: {
    id: string;
    name: string;
    email: string;
    token?: string;
  };
}

export interface RedalEnvelopeSuccess {
  status: string;
  message: string;
  content?: unknown;
}

export interface ValidateResetTokenResponse extends RedalEnvelopeSuccess {
  content?: {
    email: string;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly MEMBER_ID_KEY = 'member_id';
  private readonly MEMBER_DATA_KEY = 'member_data';

  constructor(private http: HttpClient) {}

  private async buildPayload(data: Record<string, unknown>): Promise<string> {
    const base64url = (obj: unknown) =>
      btoa(JSON.stringify(obj))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_');

    const header = base64url({ alg: 'HS256', typ: 'JWT' });
    const now = Math.floor(Date.now() / 1000);
    const claims = base64url({ ...data, iat: now, exp: now + 3600 });
    const signingInput = `${header}.${claims}`;

    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(environment.jwtSecretKeyApp),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(signingInput));
    const sigEncoded = btoa(String.fromCharCode(...new Uint8Array(signature)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');

    return `${header}.${claims}.${sigEncoded}`;
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return from(this.buildPayload({ email: credentials.email, password: credentials.password })).pipe(
      switchMap((payload) =>
        this.http.post<LoginResponse>(`${this.apiUrl}/public/app/authentication/login`, { payload })
      ),
      tap((response) => {
        if (response.status === 'SUCCESS' && response.content) {
          this.persistLoginContent(response.content);
        }
      })
    );
  }

  /**
   * Persiste access + refresh e `member_data` após login ou refresh bem-sucedido.
   */
  persistSessionFromRefreshResponse(response: LoginResponse): void {
    if (response.status === 'SUCCESS' && response.content) {
      this.persistLoginContent(response.content);
    }
  }

  private persistLoginContent(content: MemberData): void {
    if (content.token) {
      this.setToken(content.token);
    }
    if (content.refresh_token) {
      this.saveRefreshToken(content.refresh_token);
    }
    if (content.id) {
      this.saveMemberId(content.id);
      this.saveMemberData({ ...content });
    } else {
      const existing = this.getMemberData();
      if (existing) {
        this.saveMemberData({ ...existing, ...content } as MemberData);
      }
    }
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  private saveRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  /**
   * Solicita e-mail de redefinição de senha (fluxo público, sem JWT de sessão).
   */
  requestResetPassword(email: string): Observable<RedalEnvelopeSuccess> {
    return this.http.post<RedalEnvelopeSuccess>(
      `${this.apiUrl}/public/app/authentication/request_reset_password`,
      { email }
    );
  }

  /**
   * Valida o JWT do link de recuperação (assinatura + expiração).
   */
  validateResetToken(token: string): Observable<ValidateResetTokenResponse> {
    const params = new HttpParams().set('token', token);
    return this.http.get<ValidateResetTokenResponse>(
      `${this.apiUrl}/public/app/authentication/validate_reset_token`,
      { params }
    );
  }

  /**
   * Define nova senha usando o token do e-mail.
   */
  resetPassword(body: {
    token: string;
    password: string;
    password_confirmation: string;
  }): Observable<RedalEnvelopeSuccess> {
    return this.http.put<RedalEnvelopeSuccess>(
      `${this.apiUrl}/public/app/authentication/reset_password`,
      body
    );
  }

  createMember(payload: CreateMemberPayload): Observable<CreateMemberResponse> {
    const formData = new FormData();
    (Object.keys(payload) as (keyof CreateMemberPayload)[]).forEach((key) => {
      const value = payload[key];
      if (value === undefined || value === null) return;
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, String(value));
      }
    });
    return this.http.post<CreateMemberResponse>(
      `${this.apiUrl}/public/app/authentication/create_member`,
      formData
    );
  }

  /**
   * Realiza logout do usuário
   */
  logout(): void {
    this.removeToken();
    this.removeRefreshToken();
    localStorage.removeItem(this.MEMBER_ID_KEY);
    localStorage.removeItem(this.MEMBER_DATA_KEY);
  }

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Obtém o token JWT do localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Salva o token JWT no localStorage
   */
  saveToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  saveMemberId(id: string): void {
    localStorage.setItem(this.MEMBER_ID_KEY, id);
  }

  getMemberId(): string | null {
    return localStorage.getItem(this.MEMBER_ID_KEY);
  }

  saveMemberData(data: MemberData): void {
    localStorage.setItem(this.MEMBER_DATA_KEY, JSON.stringify(data));
  }

  getMemberData(): MemberData | null {
    const raw = localStorage.getItem(this.MEMBER_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  private setToken(token: string): void {
    this.saveToken(token);
  }

  /**
   * Remove o token JWT do localStorage
   */
  private removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  private removeRefreshToken(): void {
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}
